import builder from "@rsql/builder";
import { emit } from "@rsql/emitter";
import {
	ColDef,
	DateFilterModel,
	ICombinedSimpleModel,
	ISimpleFilterModel,
	NumberFilterModel,
	SetFilterModel,
	TextFilterModel,
} from "ag-grid-community";
import { IS_NOT_NULL, IS_NULL } from "../Rsql/ast";

/**
 * Stable, non-empty group key for the "(None)"/"Geen" bucket. AG Grid's SSRM `groupKeys` are strings and
 * a null/blank group value cannot round-trip (the group never issues a proper leaf request). The group
 * columns' `keyCreator` maps null/blank values to this sentinel so the null group is drillable; both
 * `groupKeysToRSQL` (leaf drilldown) and the backend's parent-group criteria translate it back to an
 * `IS NULL` filter. Kept in sync with the backend constant in GridDataController.
 */
export const GROUP_NULL_KEY = "__PULSEFOOD_GROUP_NULL__";

type GridFilterType =
	| "equals"
	| "notEqual"
	| "contains"
	| "notContains"
	| "startsWith"
	| "endsWith"
	| "lessThan"
	| "lessThanOrEqual"
	| "greaterThan"
	| "greaterThanOrEqual"
	| "inRange"
	| "blank"
	| "notBlank"
	| "empty"
	| "in"
	| "notIn"
	| "simpleSearch"
	| "advancedSearch";

/**
 * Assertions
 */
export function isCombinedFilter(filter: ISimpleFilterModel): filter is ICombinedSimpleModel<ISimpleFilterModel> {
	return "operator" in filter && (filter.operator === "AND" || filter.operator === "OR");
}

export function isTextFilter(filter: ISimpleFilterModel): filter is TextFilterModel {
	return filter.filterType === "text";
}

export function isNumberFilter(filter: ISimpleFilterModel): filter is NumberFilterModel {
	return filter.filterType === "number";
}

export function isDateFilter(filter: ISimpleFilterModel): filter is DateFilterModel {
	return filter.filterType === "date";
}

export function isSetFilter(filter: ISimpleFilterModel): filter is SetFilterModel {
	return filter.filterType === "set";
}

/**
 * Convert an ag-grid filter model to an RSQL string.
 */
export function convertFilterModelToRSQLString(
	filterModel: Record<string, ICombinedSimpleModel<ISimpleFilterModel> | ISimpleFilterModel>,
	columnDefs: ColDef[],
) {
	const comparisonNodes = Object.entries(filterModel).map(([key, fm]) => {
		const colDef = columnDefs.find((c) => c.field == key) as ColDef;
		if (isCombinedFilter(fm) && fm.conditions?.length) {
			if (fm.operator === "OR") {
				return builder.or(
					...fm.conditions.map((condition) => {
						return filterModelToComparisonNode(key, condition, colDef);
					}),
				);
			} else if (fm.operator === "AND") {
				return builder.and(
					...fm.conditions.map((condition) => {
						return filterModelToComparisonNode(key, condition, colDef);
					}),
				);
			}
		}

		return filterModelToComparisonNode(key, fm, colDef);
	});

	if (comparisonNodes.length > 0) {
		return emit(builder.and(...comparisonNodes));
	}

	return "";
}

/**
 *
 */
export function filterModelToComparisonNode(filterKey: string, filter: ISimpleFilterModel, colDef: ColDef) {
	if (isTextFilter(filter)) {
		return getComparisonNode(
			filterKey,
			// TODO: Fix TS error
			// @ts-ignore-next-line
			[filter.filter, filter.filterTo],
			filter.type,
		);
	} else if (isNumberFilter(filter)) {
		return getComparisonNode(
			filterKey,
			// TODO: Fix TS error
			// @ts-ignore-next-line
			[filter.filter, filter.filterTo],
			filter.type,
		);
	} else if (isDateFilter(filter)) {
		return getComparisonNode(
			filterKey,
			// TODO: Fix TS error
			// @ts-ignore-next-line
			[filter.dateFrom, filter.dateTo],
			filter.type,
		);
	} else if (isSetFilter(filter)) {
		if ("collection" in colDef.filterParams) {
			return builder.or(...filter.values.map((value) => builder.comparison(filterKey, "=contains=", value as string)));
		}

		// One value and it's falsy
		if (filter.values.length === 1 && !filter.values[0]) {
			return builder.comparison(filterKey, IS_NULL, "");
		}

		// Multiple values and one of them is null
		if (filter.values.includes(null)) {
			return builder.or(
				builder.comparison(filterKey, IS_NULL, ""),
				builder.in(filterKey, filter.values.filter((v) => !!v).map(String)),
			);
		}

		// No null values
		return getComparisonNode(filterKey, filter.values.map(String), "in");
	} else {
		throw new Error(`Unknown filter type: '${filter.filterType}' provided!`);
	}
}

/**
 * Read a (possibly nested) value out of a row object by a dotted path, mirroring how ag-grid resolves
 * a colDef `field` like `"product.name"`.
 */
export function getNested(obj: any, path: string): any {
	if (obj == null) return undefined;
	return path.split(".").reduce((acc, part) => (acc == null ? undefined : acc[part]), obj);
}

/**
 * Build an RSQL equality string constraining a row to the already-expanded parent group keys, so leaf
 * rows can be fetched from the normal index endpoint. Uses each column's `context.groupKeyPath`
 * (the id path for entity references) falling back to its field.
 */
export function groupKeysToRSQL(
	rowGroupCols: { id?: string; field?: string }[],
	groupKeys: (string | null)[],
	colDefs: ColDef[],
): string {
	const nodes = groupKeys.map((key, i) => {
		const groupCol = rowGroupCols[i] ?? {};
		const colDef = colDefs.find(
			(c) => c.colId === groupCol.id || c.field === groupCol.field || c.field === groupCol.id,
		);
		const ctx = colDef?.context as any;
		const keyPath = (ctx?.groupKeyPath as string) || groupCol.field || groupCol.id || "";
		if (key === null || key === undefined || key === "" || key === GROUP_NULL_KEY) {
			// Null "(None)"/"Geen" group: null-check the association (join-free FK check) for references.
			// The GROUP_NULL_KEY sentinel is what a null group's `keyCreator` emits so the group round-trips.
			return builder.comparison((ctx?.groupNullPath as string) || keyPath, IS_NULL, "");
		}
		return builder.eq(keyPath, key);
	});

	if (nodes.length === 0) return "";
	if (nodes.length === 1) return emit(nodes[0]);
	return emit(builder.and(...nodes));
}

export function getComparisonNode(key: string, filter: (string | number)[], filterType: GridFilterType) {
	switch (filterType) {
		case "equals":
			return builder.eq(key, filter[0]);
		case "notEqual":
			return builder.neq(key, filter[0]);
		case "contains":
			return builder.eq(key, `*${filter[0]}*`);
		case "notContains":
			return builder.neq(key, `*${filter[0]}*`);
		case "startsWith":
			return builder.eq(key, `${filter[0]}*`);
		case "endsWith":
			return builder.eq(key, `*${filter[0]}`);
		case "lessThan":
			return builder.lt(key, filter[0]);
		case "lessThanOrEqual":
			return builder.le(key, filter[0]);
		case "greaterThan":
			return builder.gt(key, filter[0]);
		case "greaterThanOrEqual":
			return builder.ge(key, filter[0]);
		case "inRange":
			return builder.logic([builder.ge(key, filter[0]), builder.le(key, filter[1])], "and");
		case "blank":
			return builder.comparison(key, IS_NULL, "");
		case "notBlank":
			return builder.comparison(key, IS_NOT_NULL, "");
		case "empty":
			return builder.eq(key, "");
		case "in":
			return builder.in(key, filter);
		case "simpleSearch": {
			const terms = (filter[0] as string)
				?.trim()
				.split(" ")
				.map((it) => builder.comparison(key, "==", `*${it}*`));

			return builder.and(...terms);
		}
		case "advancedSearch":
			return builder.comparison(key, "=search=", filter[0]);
		default:
			throw new Error("Invalid filter type provided!");
	}
}
